import { useEffect, useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { 
  Search, LibraryBig, CalendarClock, CalendarDays, 
  Library, Barcode, Scan, ScanBarcode, NotebookText, FolderUp, 
  ChevronLeft, ChevronRight, RefreshCw, X, RotateCcw, Plus,
  BookOpen, Clock, AlertTriangle, CheckCircle2
} from "lucide-react";
import toast, { Toaster } from "react-hot-toast";
import api from "../../connector/api";

import AppLayout from "../components/common/AppLagout";
import CheckoutModal from "../components/CheckoutModal";
import RenewModal from "../components/RenewModal";
import ReturnModal from "../components/ReturnModal";
import LoanCard from "../components/LoanCard";
import BulkCheckinModal from "../components/BulkCheckInModal";
import CheckInModal from "../components/CheckInModal";
import ReturnedBooksModal from "../components/ReturnedBooksModal";

export default function CirculationPage() {
    const navigate = useNavigate();
    const [loans, setLoans] = useState([]);
    const [page, setPage] = useState(1);
    const [count, setCount] = useState(0);
    const [pageSize] = useState(15);
    const [filters, setFilters] = useState({});
    const [search, setSearch] = useState("");
    const [summary, setSummary] = useState({ total: 0, overdue: 0, due_today: 0 });
    const [dateFilterOpen, setDateFilterOpen] = useState(false);
    const [fromDate, setFromDate] = useState("");
    const [toDate, setToDate] = useState("");
    const [loading, setLoading] = useState(false);

    // Header Global Book Search State
    const searchRef = useRef(null);
    const [headerSearchTerm, setHeaderSearchTerm] = useState("");
    const [headerSearchLoading, setHeaderSearchLoading] = useState(false);
    const [headerSearchResults, setHeaderSearchResults] = useState([]);
    const [headerShowDropdown, setHeaderShowDropdown] = useState(false);
    const [headerHighlightedIndex, setHeaderHighlightedIndex] = useState(-1);

    // Modals
    const [checkoutOpen, setCheckoutOpen] = useState(false);
    const [checkinOpen, setCheckinOpen] = useState(false);
    const [returnData, setReturnData] = useState(null);
    const [renewData, setRenewData] = useState(null);
    const [bulkCheckinOpen, setBulkCheckinOpen] = useState(false);
    const [returnedModalOpen, setReturnedModalOpen] = useState(false);

    // Helper for normalizing cover image URLs
    const getCoverUrl = (path) => {
        if (!path) return null;
        if (path.startsWith("http://") || path.startsWith("https://")) return path;
        const base = api.defaults.baseURL
            ? api.defaults.baseURL.replace(/\/api\/?$/, "")
            : "";
        const normalizedPath = path.startsWith("/") ? path : `/${path}`;
        return `${base}${normalizedPath}`;
    };

    // 1. Debounced Header Book Search Effect
    useEffect(() => {
        if (!headerSearchTerm.trim()) {
            setHeaderSearchResults([]);
            setHeaderShowDropdown(false);
            return;
        }

        setHeaderSearchLoading(true);
        const handler = setTimeout(async () => {
            try {
                const res = await api.get("/cataloging/dashboard/search", {
                    params: { q: headerSearchTerm },
                });
                const data = res.data || [];
                const normalizedResults = data.map((b) => ({
                    ...b,
                    cover_url: getCoverUrl(b.cover_image),
                    is_available: b.status === "available",
                }));
                setHeaderSearchResults(normalizedResults);
                setHeaderShowDropdown(true);
            } catch (err) {
                console.error("Header search failed:", err);
                setHeaderSearchResults([]);
            } finally {
                setHeaderSearchLoading(false);
            }
        }, 400);

        return () => clearTimeout(handler);
    }, [headerSearchTerm]);

    // Handle Selecting a Book from Header Dropdown
    const handleSelectBook = (selectedBook) => {
        const bookId = selectedBook?.id || selectedBook?._id;
        if (!bookId) return;

        setHeaderShowDropdown(false);
        setHeaderSearchTerm("");
        navigate(`/library/cataloging/book/detail/${bookId}`);
    };

    // 2. Fetch Loans from server
    const fetchLoans = async (newPage = 1, newFilters = filters, searchTerm = search) => {
        try {
            setLoading(true);
            const params = {
                page: newPage,
                page_size: pageSize,
                active: true,
                ...newFilters,
            };

            if (fromDate) params.from_date = fromDate;
            if (toDate) params.to_date = toDate;
            if (searchTerm) params.search = searchTerm;

            const res = await api.get("/loans/", { params });

            setLoans(res.data.results || []);
            setPage(newPage);
            setCount(res.data.count || 0);
        } catch (err) {
            toast.error("Failed to load active loan records");
            setLoans([]);
            setCount(0);
        } finally {
            setLoading(false);
        }
    };

    // 3. Fetch summary metrics
    const fetchSummary = async () => {
        try {
            const res = await api.get("/loans/summary/", { params: { active: true } });
            setSummary(res.data);
        } catch (err) {
            console.error(err);
            toast.error("Failed to fetch circulation summary");
            setSummary({ total: 0, overdue: 0, due_today: 0 });
        }
    };

    useEffect(() => {
        fetchLoans(page);
        fetchSummary();
    }, [page]);

    const handleClearFilters = () => {
        setFromDate("");
        setToDate("");
        setFilters({});
        setSearch("");
        setDateFilterOpen(false);
        fetchLoans(1, {}, "");
    };

    const totalPages = Math.ceil(count / pageSize) || 1;

    // Header Props configuration for AppLayout
    const headerProps = {
        title: "Borrowing & Circulation",
        searchTerm: headerSearchTerm,
        setSearchTerm: setHeaderSearchTerm,
        searchRef,
        searchLoading: headerSearchLoading,
        searchResults: headerSearchResults,
        showDropdown: headerShowDropdown,
        setShowDropdown: setHeaderShowDropdown,
        highlightedIndex: headerHighlightedIndex,
        setHighlightedIndex: setHeaderHighlightedIndex,
        handleSelectBook,
    };

    return (
        <AppLayout headerProps={headerProps}>
            <Toaster 
                position="top-right"
                toastOptions={{
                    style: {
                        background: "#1a1613",
                        color: "#f5f5f4",
                        border: "1px solid #2e2720"
                    }
                }}
            />

            <div className="space-y-6 text-stone-200 pb-8 font-dynamic-title">

                {/* TOP TOOLBAR & QUICK ACTIONS BAR */}
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 bg-[#1a1613] p-4 rounded-2xl border border-[#2e2720]">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-amber-500/10 border border-amber-500/20 flex items-center justify-center text-amber-400">
                            <ScanBarcode className="w-5 h-5" />
                        </div>
                        <div>
                            <h1 className="text-base font-bold text-stone-100">Circulation & Fulfillment</h1>
                            <p className="text-[11px] text-stone-400">Manage active checkouts, check-ins, and return logs</p>
                        </div>
                    </div>

                    {/* Action Buttons Toolbar */}
                    <div className="flex flex-wrap items-center gap-2">
                        <button
                            onClick={() => setCheckoutOpen(true)}
                            className="flex items-center gap-2 px-3.5 h-9 bg-amber-500 hover:bg-amber-400 text-stone-950 text-xs font-bold rounded-xl shadow transition"
                        >
                            <Barcode className="h-4 w-4 stroke-[2.5]" /> Check Out
                        </button>
                        
                        <button
                            onClick={() => setCheckinOpen(true)}
                            className="flex items-center gap-2 px-3.5 h-9 bg-[#131110] border border-[#2e2720] text-stone-300 hover:text-amber-400 hover:border-amber-500/30 text-xs font-bold rounded-xl transition"
                        >
                            <Scan className="h-4 w-4 text-stone-400" /> Check In
                        </button>

                        <button
                            onClick={() => setBulkCheckinOpen(true)}
                            className="flex items-center gap-2 px-3.5 h-9 bg-[#131110] border border-[#2e2720] text-stone-300 hover:text-amber-400 hover:border-amber-500/30 text-xs font-bold rounded-xl transition"
                        >
                            <FolderUp className="h-4 w-4 text-stone-400" /> Bulk Check-In
                        </button>

                        <button
                            onClick={() => setReturnedModalOpen(true)}
                            className="flex items-center gap-2 px-3.5 h-9 bg-[#131110] border border-[#2e2720] text-stone-300 hover:text-amber-400 hover:border-amber-500/30 text-xs font-bold rounded-xl transition"
                        >
                            <NotebookText className="h-4 w-4 text-stone-400" /> Show Returned
                        </button>
                    </div>
                </div>

                {/* INTERACTIVE METRIC CARDS */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    
                    {/* Active Loans Card */}
                    <div 
                        onClick={() => {
                            const newFilters = { active: "true" };
                            setFilters(newFilters);
                            fetchLoans(1, newFilters, search);
                        }}
                        className={`group relative bg-[#1a1613] p-5 rounded-2xl border transition-all cursor-pointer shadow-md ${
                            Object.keys(filters).length === 0 || filters.active 
                                ? "border-amber-500/60 ring-2 ring-amber-500/10" 
                                : "border-[#2e2720] hover:border-stone-600"
                        }`}
                    >
                        <p className="text-[10px] font-bold text-stone-400 uppercase tracking-wider">Active Loans</p>
                        <p className="text-xl font-bold text-stone-100 mt-2">{summary.total}</p>
                        <div className="absolute top-5 right-5 p-2.5 bg-[#131110] border border-[#2e2720] text-amber-400 group-hover:border-amber-500/30 rounded-xl transition">
                            <LibraryBig className="h-5 w-5" />
                        </div>
                    </div>

                    {/* Overdue Defaulters Card */}
                    <div 
                        className={`relative bg-[#1a1613] p-5 rounded-2xl border transition-all shadow-md ${
                            filters.filter === "overdue" 
                                ? "border-rose-500/60 ring-2 ring-rose-500/10" 
                                : "border-[#2e2720] hover:border-stone-600 border-l-4 border-l-rose-500"
                        }`}
                    >
                        <div 
                            className="cursor-pointer group"
                            onClick={() => {
                                setFilters({ filter: "overdue" });
                                setDateFilterOpen(!dateFilterOpen);
                            }}
                        >
                            <p className="text-[10px] font-bold text-rose-400 uppercase tracking-wider">Overdue Defaulters</p>
                            <p className="text-xl font-bold text-rose-400 mt-2">{summary.overdue}</p>
                            <div className="absolute top-5 right-5 p-2.5 bg-rose-500/10 border border-rose-500/20 text-rose-400 rounded-xl transition">
                                <CalendarClock className="h-5 w-5" />
                            </div>
                        </div>

                        {/* Overdue Date Range Popover */}
                        {dateFilterOpen && (
                            <div className="absolute right-0 top-[95px] bg-[#1a1613] shadow-2xl rounded-2xl border border-[#2e2720] p-4 z-30 flex flex-col gap-3 w-64 animate-fadeIn">
                                <div className="flex items-center justify-between border-b border-[#2e2720] pb-2">
                                    <span className="font-bold text-xs text-stone-200 uppercase tracking-wide">Filter Overdue Range</span>
                                    <X className="h-4 w-4 text-stone-400 cursor-pointer hover:text-stone-200" onClick={() => setDateFilterOpen(false)} />
                                </div>
                                <div className="space-y-2">
                                    <div>
                                        <label className="text-[10px] font-bold text-stone-400 uppercase">From Date</label>
                                        <input
                                            type="date"
                                            className="w-full bg-[#131110] border border-[#2e2720] px-2.5 py-1.5 rounded-xl text-xs text-stone-200 focus:outline-none focus:border-amber-500/50 transition mt-1 font-mono"
                                            value={fromDate}
                                            onChange={(e) => setFromDate(e.target.value)}
                                        />
                                    </div>
                                    <div>
                                        <label className="text-[10px] font-bold text-stone-400 uppercase">To Date</label>
                                        <input
                                            type="date"
                                            className="w-full bg-[#131110] border border-[#2e2720] px-2.5 py-1.5 rounded-xl text-xs text-stone-200 focus:outline-none focus:border-amber-500/50 transition mt-1 font-mono"
                                            value={toDate}
                                            onChange={(e) => setToDate(e.target.value)}
                                        />
                                    </div>
                                </div>
                                <div className="flex justify-end gap-2 pt-2 border-t border-[#2e2720]">
                                    <button
                                        className="px-3 py-1.5 bg-[#131110] border border-[#2e2720] text-stone-300 rounded-xl text-xs font-bold hover:text-amber-400 transition"
                                        onClick={handleClearFilters}
                                    >
                                        Reset
                                    </button>
                                    <button
                                        className="px-3 py-1.5 bg-rose-600 hover:bg-rose-500 text-white rounded-xl text-xs font-bold transition shadow"
                                        onClick={() => {
                                            setDateFilterOpen(false);
                                            fetchLoans(1, { ...filters }, search);
                                        }}
                                    >
                                        Apply
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Maturing Due Today Card */}
                    <div 
                        onClick={() => {
                            const newFilters = { filter: "due_today" };
                            setFilters(newFilters);
                            fetchLoans(1, newFilters, search);
                        }}
                        className={`group relative bg-[#1a1613] p-5 rounded-2xl border transition-all cursor-pointer shadow-md ${
                            filters.filter === "due_today" 
                                ? "border-amber-500/60 ring-2 ring-amber-500/10" 
                                : "border-[#2e2720] hover:border-stone-600 border-l-4 border-l-amber-500"
                        }`}
                    >
                        <p className="text-[10px] font-bold text-amber-400 uppercase tracking-wider">Maturing Due Today</p>
                        <p className="text-xl font-bold text-amber-400 mt-2">{summary.due_today}</p>
                        <div className="absolute top-5 right-5 p-2.5 bg-amber-500/10 border border-amber-500/20 text-amber-400 rounded-xl transition">
                            <CalendarDays className="h-5 w-5" />
                        </div>
                    </div>
                </div>

                {/* IN-PAGE SEARCH & CLEAR FILTERS CONTROL PANEL */}
                <div className="bg-[#1a1613] border border-[#2e2720] rounded-2xl p-4 flex flex-col md:flex-row items-center gap-3">
                    <div className="relative flex-1 w-full">
                        <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-stone-500" />
                        <input
                            type="text"
                            placeholder="Search active loans by member name, card number, ISBN barcode, or title..."
                            className="w-full bg-[#131110] border border-[#2e2720] pl-10 pr-10 py-2 rounded-xl text-xs font-semibold text-stone-100 placeholder-stone-500 focus:outline-none focus:border-amber-500/50 transition"
                            value={search}
                            onChange={(e) => {
                                const value = e.target.value;
                                setSearch(value);
                                fetchLoans(1, filters, value);
                            }}
                        />
                        {search && (
                            <button 
                                onClick={() => { setSearch(""); fetchLoans(1, filters, ""); }}
                                className="absolute right-3.5 top-1/2 -translate-y-1/2 text-stone-500 hover:text-stone-300 transition"
                            >
                                <X className="h-4 w-4" />
                            </button>
                        )}
                    </div>
                    
                    {(Object.keys(filters).length > 0 || fromDate || toDate) && (
                        <button
                            onClick={handleClearFilters}
                            className="flex items-center justify-center gap-1.5 px-3.5 py-2 bg-[#131110] border border-[#2e2720] text-amber-400 hover:bg-[#201c17] rounded-xl text-xs font-bold transition w-full md:w-auto"
                        >
                            <RotateCcw className="h-3.5 w-3.5" /> Clear Filters
                        </button>
                    )}
                </div>

                {/* LOAN ACTIVITY RECORD LIST */}
                <div className="min-h-[380px]">
                    {loading ? (
                        <div className="bg-[#1a1613] border border-[#2e2720] rounded-2xl p-16 text-center text-xs text-stone-400 flex flex-col items-center justify-center gap-3">
                            <div className="h-6 w-6 border-2 border-amber-500 border-t-transparent rounded-full animate-spin" />
                            <span>Retrieving loan activity logs...</span>
                        </div>
                    ) : loans.length === 0 ? (
                        <div className="bg-[#1a1613] border border-[#2e2720] rounded-2xl p-16 text-center flex flex-col items-center justify-center gap-3">
                            <div className="p-3 bg-[#131110] border border-[#2e2720] text-stone-500 rounded-2xl">
                                <Library className="h-8 w-8" />
                            </div>
                            <div>
                                <p className="font-bold text-stone-200 text-sm">No Active Circulation Logs Found</p>
                                <p className="text-xs text-stone-500 mt-1">No borrowed resource records match your query parameters.</p>
                            </div>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 gap-3">
                            {loans.map((loan) => (
                                <div key={loan.id} className="bg-[#1a1613] rounded-2xl border border-[#2e2720] hover:border-amber-500/40 transition-all overflow-hidden shadow-md">
                                    <LoanCard
                                        loan={{ ...loan }}
                                        returned={!!loan.returned_date}
                                        onReturn={() => setReturnData(loan)}
                                        onRenew={() => setRenewData(loan)}
                                    />
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* PAGINATION FOOTER */}
                <div className="bg-[#1a1613] border border-[#2e2720] px-5 py-3 rounded-2xl flex items-center justify-between text-xs font-semibold text-stone-400">
                    <button
                        disabled={page === 1 || loading}
                        onClick={() => fetchLoans(page - 1, filters, search)}
                        className="w-8 h-8 flex items-center justify-center bg-[#131110] border border-[#2e2720] rounded-xl text-stone-300 hover:text-amber-400 disabled:opacity-40 disabled:hover:text-stone-300 transition"
                    >
                        <ChevronLeft className="h-4 w-4" />
                    </button>
                    
                    <span>
                        Showing <strong className="text-stone-200">{count === 0 ? 0 : (page - 1) * pageSize + 1}</strong> to <strong className="text-stone-200">{Math.min(page * pageSize, count)}</strong> of <strong className="text-stone-200">{count}</strong> records
                    </span>
                    
                    <button
                        disabled={page >= totalPages || loading}
                        onClick={() => fetchLoans(page + 1, filters, search)}
                        className="w-8 h-8 flex items-center justify-center bg-[#131110] border border-[#2e2720] rounded-xl text-stone-300 hover:text-amber-400 disabled:opacity-40 disabled:hover:text-stone-300 transition"
                    >
                        <ChevronRight className="h-4 w-4" />
                    </button>
                </div>

            </div>

            {/* MODALS PERSISTENCE LAYER */}
            <CheckoutModal
                open={checkoutOpen}
                onClose={() => setCheckoutOpen(false)}
                onSuccess={fetchLoans}
            />
            {checkinOpen && (
                <CheckInModal
                    onClose={() => setCheckinOpen(false)}
                    onSuccess={() => fetchLoans(page, filters, search)}
                />
            )}
            <ReturnModal
                open={!!returnData}
                loan={returnData}
                onClose={() => setReturnData(null)}
                onReturnSuccess={async (loan) => {
                    try {
                        await api.post(`/loans/${loan.id}/return/`);
                        setLoans(prev => prev.filter(l => l.id !== loan.id));
                        setSummary(prev => ({
                            ...prev,
                            total: prev.total - 1,
                            overdue: prev.overdue - (loan.is_overdue ? 1 : 0),
                            due_today: prev.due_today - (loan.is_due_today ? 1 : 0)
                        }));
                        toast.success(`Book "${loan.book?.title || loan.title}" returned successfully`);
                        setReturnData(null);
                    } catch (err) {
                        console.error(err);
                        toast.error("Failed to return book");
                    }
                }}
            />
            {returnedModalOpen && (
                <ReturnedBooksModal onClose={() => setReturnedModalOpen(false)} />
            )}
            <RenewModal
                open={!!renewData}
                loan={renewData}
                onClose={() => setRenewData(null)}
                onSuccess={fetchLoans}
            />
            {bulkCheckinOpen && (
                <BulkCheckinModal
                    onClose={() => setBulkCheckinOpen(false)}
                    onSuccess={fetchLoans}
                />
            )}
        </AppLayout>
    );
}